import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguageData } from '../contexts/LanguageContext';
import './PracticeModal.css';

const PracticeModal = ({ isOpen, onClose, language, proficiencyLevel }) => {
  const navigate = useNavigate();
  
  // Map full language names to codes
  const languageCodeMap = {
    'german': 'de',
    'english': 'en',
    'french': 'fr',
    'italian': 'it',
    'spanish': 'es',
    'japanese': 'ja'
  };
  
  // Convert language to code if it's a full name
  const initialLanguageCode = languageCodeMap[language?.toLowerCase()] || language || 'de';
  
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguageCode);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [dialect, setDialect] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(''); // No default selection
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isSkillOpen, setIsSkillOpen] = useState(false);

  // Reset all modal state when modal opens
  useEffect(() => {
    if (isOpen) {
      const code = language ? (languageCodeMap[language.toLowerCase()] || language) : 'de';
      setSelectedLanguage(code);
      setSelectedSkill('');
      setDialect('');
      setSelectedAvatar('');
      setIsLanguageOpen(false);
      setIsSkillOpen(false);
    }
  }, [isOpen, language]);

  // Reset dependent fields when skill changes
  useEffect(() => {
    // Reset dialect for Writing (doesn't need it)
    if (selectedSkill === 'Writing') {
      setDialect('');
    }
    // Reset avatar for Writing only (Listening needs avatar now)
    if (selectedSkill === 'Writing') {
      setSelectedAvatar('');
    }
  }, [selectedSkill]);

  const { languages: languagesData } = useLanguageData();

  if (!isOpen) return null;

  // Determine which fields to show based on selected skill
  // Show dialect and avatar by default when no skill is selected, or for specific skills
  const showDialect = !selectedSkill || selectedSkill === 'Reading' || selectedSkill === 'Speaking' || selectedSkill === 'Listening';
  const showAvatar = !selectedSkill || selectedSkill === 'Reading' || selectedSkill === 'Speaking' || selectedSkill === 'Listening';
  const isSubmitDisabled = !selectedSkill; // Disable submit if no skill selected

  const handleSubmit = () => {
    onClose();
    
    // Navigate to different routes based on selected skill
    const routeMap = {
      'Speaking': '/practice-speaking',
      'Reading': '/practice-reading',
      'Writing': '/practice-writing',
      'Listening': '/practice-listening'
    };
    
    const route = routeMap[selectedSkill] || '/practice-speaking';
    
    // Convert language code back to full name for consistency
    const codeToNameMap = {
      'de': 'german',
      'en': 'english',
      'fr': 'french',
      'it': 'italian',
      'es': 'spanish',
      'ja': 'japanese'
    };
    
    const languageName = codeToNameMap[selectedLanguage] || selectedLanguage;
    
    navigate(route, { 
      state: { 
        language: languageName, 
        proficiencyLevel,
        scenario: 'rwsl',
        skill: selectedSkill,
        dialect,
        avatar: selectedAvatar
      } 
    });
  };

  const languageOptions = Object.entries(languagesData)
    .map(([code, data]) => ({
      value: code,
      label: data.label,
      flag: data.flag || `/images/flag-${code}.svg`,
      active: data.active !== false
    }));

  const skillOptions = [
    'Reading',
    'Writing',
    'Listening',
    'Speaking'
  ];

  const currentLanguage = languageOptions.find(l => l.value === selectedLanguage);

  return (
    <>
      <div className="practice-modal-overlay"></div>
      <div className="practice-modal">
        <div className="practice-modal-header">
          <h2 className="practice-modal-title">
            Language Skills (Reading, Writing, Listening, Speaking)
          </h2>
        </div>

        <p className="practice-modal-description">
          Integrated language development across reading, writing, speaking and listening
        </p>

        <div className="practice-modal-form">
          <div className="practice-modal-field">
            <label className="practice-modal-label">
              <span className="practice-modal-required">*</span> Language Skill
            </label>
            <div className="practice-modal-custom-select">
              <div 
                className="practice-modal-select practice-modal-select-trigger"
                onClick={() => { setIsSkillOpen(!isSkillOpen); setIsLanguageOpen(false); }}
              >
                <span className="practice-modal-select-text">
                  {selectedSkill || 'Select Skill'}
                </span>
                <svg className="practice-modal-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#555555" strokeWidth="1.5"/>
                </svg>
              </div>
              {isSkillOpen && (
                <div className="practice-modal-dropdown">
                  {skillOptions.map(skill => (
                    <div
                      key={skill}
                      className={`practice-modal-dropdown-item ${selectedSkill === skill ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSkill(skill);
                        setIsSkillOpen(false);
                      }}
                    >
                      <span>{skill}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="practice-modal-field">
            <label className="practice-modal-label">
              <span className="practice-modal-required">*</span> Language
            </label>
            <div className="practice-modal-custom-select">
              <div 
                className="practice-modal-select practice-modal-select-trigger"
                onClick={() => { setIsLanguageOpen(!isLanguageOpen); setIsSkillOpen(false); }}
              >
                <img 
                  src={currentLanguage?.flag} 
                  alt={currentLanguage?.label}
                  className="practice-modal-flag"
                />
                <span className="practice-modal-select-text">
                  {currentLanguage?.label || 'Select Language'}
                </span>
                <svg className="practice-modal-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#555555" strokeWidth="1.5"/>
                </svg>
              </div>
              {isLanguageOpen && (
                <div className="practice-modal-dropdown">
                  {languageOptions.map(option => (
                    <div
                      key={option.value}
                      className={`practice-modal-dropdown-item ${selectedLanguage === option.value ? 'selected' : ''} ${!option.active ? 'inactive' : ''}`}
                      onClick={() => {
                        if (!option.active) return;
                        setSelectedLanguage(option.value);
                        setIsLanguageOpen(false);
                      }}
                    >
                      <img 
                        src={option.flag} 
                        alt={option.label}
                        className="practice-modal-flag"
                      />
                      <span>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {showDialect && (
            <div className="practice-modal-field practice-modal-field-disabled">
              <label className="practice-modal-label">
                Select your AI avatar Dialects <span className="coming-soon-text">Coming Soon<img src="/images/Time.svg" alt="" className="time-icon" /></span>
              </label>
              <div className="practice-modal-select" style={{ opacity: 0.5, pointerEvents: 'none' }}>
                <select 
                  value={dialect}
                  disabled
                  className="practice-modal-select-input"
                >
                  <option value="">Select a dialect.</option>
                  <option value="standard">Standard</option>
                  <option value="regional">Regional</option>
                  <option value="formal">Formal</option>
                  <option value="informal">Informal</option>
                </select>
                <svg className="practice-modal-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#555555" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
          )}
        </div>

        {showAvatar && (
          <div className="practice-modal-avatars">
            <h3 className="practice-modal-avatars-title">Choose AI Avatar!</h3>
            
            <div className="practice-modal-avatars-grid">
              <div 
                className={`practice-modal-avatar-card ${selectedAvatar === 'Max-business.mp4' ? 'selected' : ''}`}
                onClick={() => setSelectedAvatar('Max-business.mp4')}
              >
                <img 
                  src="/images/MaleAvatar.svg" 
                  alt="Soren"
                  className="practice-modal-avatar-image"
                />
                <div className="practice-modal-avatar-radio">
                  <span className="practice-modal-radio-custom"></span>
                  <label className="practice-modal-radio-label">Soren</label>
                </div>
              </div>

              <div 
                className={`practice-modal-avatar-card ${selectedAvatar === 'talkingavater.mp4' ? 'selected' : ''}`}
                onClick={() => setSelectedAvatar('talkingavater.mp4')}
              >
                <img 
                  src="/images/FemaleAvatar.svg" 
                  alt="Nebula"
                  className="practice-modal-avatar-image"
                />
                <div className="practice-modal-avatar-radio">
                  <span className="practice-modal-radio-custom"></span>
                  <label className="practice-modal-radio-label">Nebula</label>
                </div>
              </div>
            </div>


          </div>
        )}

        <div className="practice-modal-footer">
          <button className="practice-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="practice-modal-submit" 
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              opacity: isSubmitDisabled ? 0.5 : 1,
              cursor: isSubmitDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            Submit
          </button>
        </div>
      </div>
    </>
  );
};

export default PracticeModal;
